import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import '../styles/LoginPage.css';
import ValidationNotification from '../components/ValidationNotification';
import { signInUser, isValidEmail } from '../services/userService';
import { useAuth } from '../context/AuthContext';

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const { setRememberMe } = useAuth();
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMeChecked, setRememberMeChecked] = useState(false);
  const [notification, setNotification] = useState({
    message: '',
    isVisible: false
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const showNotification = (message: string) => {
    setNotification({
      message,
      isVisible: true
    });
    
    // Auto-hide notification after 5 seconds
    setTimeout(() => {
      hideNotification();
    }, 5000);
  };

  const hideNotification = () => {
    setNotification({
      ...notification,
      isVisible: false
    });
  };

  const validateForm = (): boolean => {
    // Check required fields
    if (!formData.email || !formData.password) {
      showNotification("Email og Password er påkrævede felter");
      return false;
    }

    // Email validation
    if (!isValidEmail(formData.email)) {
      showNotification("Skriv venligst en valid e-mail");
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate form before submission
    if (!validateForm()) {
      return;
    }
    
    try {
      setLoading(true);
      
      // Call the sign in service
      await signInUser(formData.email, formData.password);
      
      // Set remember me preference
      setRememberMe(rememberMeChecked);
      
      // Navigate to main page after successful login
      navigate('/main');
    } catch (error: any) {
      console.error('Error during login:', error);
      showNotification("Vi kan ikke finde din e-mail - forsøg med én anden e-mail eller lav en bruger her");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      {/* Background Image */}
      <div className="login-background"></div>

      {/* Validation Notification */}
      <ValidationNotification
        message={notification.message}
        isVisible={notification.isVisible}
        onClose={hideNotification}
      />

      {/* Logo */}
      <img src="/images/logo.png" alt="Wiin Logo" className="login-logo" />

      {/* Welcome Text */}
      <h1 className="login-heading">Login og Wiin</h1>
      <p className="login-subheading">Brug din e-mail tilknyttet Wiin at logge ind</p>

      {/* Login Form */}
      <form onSubmit={handleSubmit} className="login-form">
        <div className="login-field">
          <input
            type="email"
            name="email"
            placeholder="Email"
            value={formData.email}
            onChange={handleInputChange}
            required
          />
        </div>
        <div className="login-field">
          <div className="password-input-container">
            <input
              type={showPassword ? "text" : "password"}
              name="password"
              placeholder="Password"
              value={formData.password}
              onChange={handleInputChange}
              required
            />
            <button
              type="button"
              className="password-toggle"
              onClick={() => setShowPassword(!showPassword)}
              aria-label={showPassword ? "Hide password" : "Show password"}
            >
              {showPassword ? (
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 4.5C7 4.5 2.73 7.61 1 12C2.73 16.39 7 19.5 12 19.5C17 19.5 21.27 16.39 23 12C21.27 7.61 17 4.5 12 4.5ZM12 17C9.24 17 7 14.76 7 12C7 9.24 9.24 7 12 7C14.76 7 17 9.24 17 12C17 14.76 14.76 17 12 17ZM12 9C10.34 9 9 10.34 9 12C9 13.66 10.34 15 12 15C13.66 15 15 13.66 15 12C15 10.34 13.66 9 12 9Z" fill="#9E9E9E"/>
                </svg>
              ) : (
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 19.5C17 19.5 21.27 16.39 23 12C21.27 7.61 17 4.5 12 4.5C7 4.5 2.73 7.61 1 12C2.73 16.39 7 19.5 12 19.5ZM12 7C14.76 7 17 9.24 17 12C17 14.76 14.76 17 12 17C9.24 17 7 14.76 7 12C7 9.24 9.24 7 12 7ZM12 15C13.66 15 15 13.66 15 12C15 10.34 13.66 9 12 9C10.34 9 9 10.34 9 12C9 13.66 10.34 15 12 15Z" fill="#9E9E9E"/>
                </svg>
              )}
            </button>
          </div>
        </div>

        {/* Remember Me Checkbox */}
        <div className="remember-me-container">
          <label className="remember-me-label">
            <input
              type="checkbox"
              checked={rememberMeChecked}
              onChange={(e) => setRememberMeChecked(e.target.checked)}
              className="remember-me-checkbox"
            />
            <span className="remember-me-text">Forbliv logget ind (30 dage)</span>
          </label>
        </div>

        {/* Primary Button */}
        <button 
          type="submit" 
          className="login-button"
          disabled={loading}
        >
          <span>{loading ? 'Logger ind...' : 'Login'}</span>
          <svg
            width="28"
            height="28"
            viewBox="0 0 28 28"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M18.67 9.07L23.67 13.35L18.67 17.63"
              stroke="#FFFFFF"
              strokeWidth="1.5"
              strokeMiterlimit="10"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M2.09 13.35H23.67"
              stroke="#FFFFFF"
              strokeWidth="1.5"
              strokeMiterlimit="10"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      </form>

      {/* Forgot Password Link */}
      <p className="forgot-password-link">
        <span className="login-highlight">Har du glemt login - klik her</span>
      </p>

      {/* Footer Text */}
      <p className="login-footer">
        Har du ikke en profil?
        <br />
        <span className="login-highlight" onClick={() => navigate('/create-profile')}>Opret profil</span>
      </p>
    </div>
  );
};

export default LoginPage; 